import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Scale, FileText } from "lucide-react";
import { fmtRM, monthLabel, BATCH_STATUS, RECON_FIELD_LABELS, RECON_STATUS } from "@/pages/hr/payroll/payrollUtils";

export default function BatchDetail({ batchId, onBack }) {
  const { can } = useAuth();
  const [batch, setBatch] = useState(null);
  const [lines, setLines] = useState([]);
  const [line, setLine] = useState(null);
  const [view, setView] = useState("lines"); // lines | recon
  const [recon, setRecon] = useState(null);

  const load = () => {
    api.get(`/hr/payroll/batches/${batchId}`).then((r) => setBatch(r.data)).catch(() => {});
    api.get(`/hr/payroll/batches/${batchId}/lines`).then((r) => setLines(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [batchId]);
  const loadRecon = () => api.get(`/hr/payroll/batches/${batchId}/reconciliation`).then((r) => setRecon(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { if (view === "recon") loadRecon(); /* eslint-disable-next-line */ }, [view]);

  const st = batch ? (BATCH_STATUS[batch.status] || { label: batch.status, cls: "" }) : {};

  return (
    <div data-testid="payroll-batch-detail">
      <div className="flex items-center gap-3 mb-3">
        <Button size="sm" variant="outline" onClick={onBack} data-testid="batch-detail-back"><ArrowLeft className="w-4 h-4 mr-1" />Batches</Button>
        {batch && <>
          <h3 className="text-base font-semibold">{monthLabel(batch.year, batch.month)}</h3>
          <Badge variant="outline" className={st.cls}>{st.label}</Badge>
          <Badge variant="outline" className={batch.mode === "shadow" ? "text-amber-700 border-amber-300" : "text-emerald-700 border-emerald-300"}>{batch.mode}</Badge>
        </>}
        <div className="ml-auto flex gap-1">
          <button data-testid="batch-view-lines" onClick={() => setView("lines")} className={`px-3 py-1.5 text-sm rounded-md ${view === "lines" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Employee Payroll</button>
          <button data-testid="batch-view-recon" onClick={() => setView("recon")} className={`px-3 py-1.5 text-sm rounded-md ${view === "recon" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}><Scale className="w-3.5 h-3.5 inline mr-1" />AutoCount Comparison</button>
        </div>
      </div>

      {view === "lines" && (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="excel-table w-full" data-testid="payroll-lines-table">
            <thead><tr><th>Employee</th><th>Dept</th><th>Gross</th><th>Deductions</th><th>Net Pay</th><th>Review</th><th></th></tr></thead>
            <tbody>
              {lines.length === 0 ? <tr><td colSpan={7} className="text-center text-slate-400 py-6">No calculated lines. Run Calculate on this batch.</td></tr>
                : lines.map((l) => (
                  <tr key={l.id} data-testid={`payroll-line-row-${l.employee_id}`}>
                    <td><span className="font-mono text-xs">{l.employee_id}</span> {l.employee_name}</td>
                    <td className="text-xs">{l.department || "—"}</td>
                    <td>{fmtRM(l.gross_pay)}</td>
                    <td>{fmtRM(l.total_deductions)}</td>
                    <td className="font-semibold">{fmtRM(l.net_pay)}</td>
                    <td className="text-center">{l.requires_review ? <Badge variant="outline" className="text-amber-700 border-amber-300"><AlertTriangle className="w-3 h-3 mr-1" />{(l.review_flags || []).length}</Badge> : <Badge variant="outline" className="text-emerald-700 border-emerald-300">OK</Badge>}</td>
                    <td><Button size="sm" variant="outline" data-testid={`payroll-line-open-${l.employee_id}`} onClick={() => setLine(l)}><FileText className="w-3.5 h-3.5 mr-1" />Breakdown</Button></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "recon" && recon && (
        <ReconPanel recon={recon} batchId={batchId} canEdit={can("payroll:review")} onSaved={loadRecon} />
      )}

      {line && <LineBreakdown line={line} onClose={() => setLine(null)} />}
    </div>
  );
}

function LineBreakdown({ line, onClose }) {
  const aa = line.attendance_allowance;
  const ul = line.unpaid_leave_deduction;
  const stat = line.statutory || {};
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="payroll-line-breakdown">
        <DialogHeader><DialogTitle>{line.employee_id} · {line.employee_name}</DialogTitle>
          <DialogDescription>{line.department || "—"} · {line.position || "—"} — full payroll calculation breakdown.</DialogDescription></DialogHeader>

        {line.amounts_hidden ? <p className="text-sm text-slate-500">Amounts hidden — requires HR: View Sensitive permission.</p> : (
          <div className="space-y-4 text-sm">
            {(line.review_flags || []).length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5" data-testid="line-review-flags">
                <p className="font-semibold text-amber-800 flex items-center gap-1 mb-1"><AlertTriangle className="w-4 h-4" />Requires Review</p>
                <ul className="list-disc list-inside text-amber-800 text-xs space-y-0.5">{line.review_flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
              </div>
            )}

            <Section title="Earnings">
              {(line.earnings || []).map((e, i) => <Row key={i} label={e.name + (e.manual ? " (manual)" : "")} value={e.amount} />)}
              <Row label="Gross Pay" value={line.gross_pay} bold />
            </Section>

            {aa && (
              <Section title="Attendance Allowance Breakdown">
                <Row label="Attendance Allowance Base" value={aa.base} />
                {(aa.lines || []).map((l, i) => (
                  <Row key={i} label={`${l.leave_type_code}: ${l.full_days} day(s)${l.half_days ? ` + ${l.half_days} half` : ""}${l.amount_per_day ? ` × RM${l.amount_per_day}` : ""}${l.note ? ` — ${l.note}` : ""}`} value={l.deduction ? -l.deduction : 0} />
                ))}
                <Row label="UL Full Forfeiture" text={aa.ul_forfeit ? "Yes" : "No"} />
                {aa.capped && <Row label="(Deduction capped at base — cannot go below RM0)" text="" />}
                <Row label="Attendance Allowance Payable" value={aa.payable} bold />
                {aa.requires_review && <p className="text-xs text-amber-700">⚠ {aa.review_reasons.join("; ")}</p>}
              </Section>
            )}

            {ul && (
              <Section title="Unpaid Leave Deduction (formula requires confirmation)">
                <Row label="Basic Salary" value={ul.basic_salary} />
                <Row label="Unpaid Leave Days" text={ul.ul_days} />
                <Row label="Payroll Rule" text={ul.rule} />
                <Row label="Calculated UL Deduction" value={-ul.amount} bold />
              </Section>
            )}

            <Section title="Deductions">
              {(line.deductions || []).map((d, i) => <Row key={i} label={d.name} value={-d.amount} />)}
              <Row label="Total Deductions" value={-line.total_deductions} bold />
            </Section>

            <Section title="Statutory (basis amounts)">
              {stat.epf && <Row label={`EPF — basis ${stat.epf.basis} (wage RM${stat.epf.wage})`} text={`EE ${fmtRM(stat.epf.employee)} · ER ${fmtRM(stat.epf.employer)}`} />}
              {stat.socso && <Row label={`SOCSO — assumed wage RM${stat.socso.assumed_wage}`} text={`EE ${fmtRM(stat.socso.employee)} · ER ${fmtRM(stat.socso.employer)}`} />}
              {stat.eis && <Row label={`EIS — assumed wage RM${stat.eis.assumed_wage}`} text={`EE ${fmtRM(stat.eis.employee)} · ER ${fmtRM(stat.eis.employer)}`} />}
              {stat.pcb && <Row label={`PCB — ${stat.pcb.engine_status}`} text={fmtRM(stat.pcb.amount)} />}
              {stat.pcb && stat.pcb.note && <p className="text-xs text-amber-700">{stat.pcb.note}</p>}
            </Section>

            <Section title="Net Pay">
              <Row label="Net Pay" value={line.net_pay} bold />
            </Section>

            <Section title="Employer Statutory Cost">
              <Row label="EPF (Employer)" value={line.employer_cost?.epf_employer} />
              <Row label="SOCSO (Employer)" value={line.employer_cost?.socso_employer} />
              <Row label="EIS (Employer)" value={line.employer_cost?.eis_employer} />
            </Section>

            <Section title="Attendance Completeness (operational — no auto salary deduction)">
              <Row label="Scheduled Working Days" text={line.snapshot?.attendance?.scheduled_working_days ?? "—"} />
              <Row label="Present Days" text={line.snapshot?.attendance?.present_days ?? 0} />
              <Row label="Approved Paid Leave Days" text={line.snapshot?.attendance?.paid_leave_days ?? 0} />
              <Row label="Approved Unpaid Leave Days" text={line.snapshot?.attendance?.unpaid_leave_days ?? 0} />
              <Row label="Off / Public Holiday Days" text={line.snapshot?.attendance?.off_ph_days ?? 0} />
              <Row label="Approved Exception Days" text={line.snapshot?.attendance?.approved_exception_days ?? 0} />
              <Row label="Late Minutes" text={line.snapshot?.attendance?.late_minutes ?? 0} />
              <div className="flex justify-between px-3 py-1.5 font-semibold bg-slate-50">
                <span className="text-slate-600">Unresolved Days</span>
                <span className={line.snapshot?.attendance?.unresolved_days ? "text-amber-600" : "text-emerald-600"}>
                  {line.snapshot?.attendance?.unresolved_days ?? 0}{line.snapshot?.attendance?.unresolved_days ? " — Attendance Review Required" : ""}
                </span>
              </div>
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }) => (
  <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{title}</p>
    <div className="rounded-md border border-slate-200 divide-y divide-slate-100">{children}</div></div>
);
const Row = ({ label, value, text, bold }) => (
  <div className={`flex justify-between px-3 py-1.5 ${bold ? "font-semibold bg-slate-50" : ""}`}>
    <span className="text-slate-600">{label}</span>
    <span className={value < 0 ? "text-rose-600" : ""}>{text !== undefined ? text : fmtRM(value)}</span>
  </div>
);

function ReconPanel({ recon, batchId, canEdit, onSaved }) {
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [statusEdit, setStatusEdit] = useState(null);
  const [sForm, setSForm] = useState({ status: "", reason: "", resolution_note: "" });
  const [busy, setBusy] = useState(false);
  const fields = recon.fields || [];

  const saveAc = async () => {
    setBusy(true);
    try {
      const ac = {};
      fields.forEach((f) => { if (form[f] !== undefined && form[f] !== "") ac[f] = Number(form[f]); });
      await api.post("/hr/payroll/reconciliation", { batch_id: batchId, employee_id: edit, autocount: ac });
      toast.success("AutoCount values saved"); setEdit(null); setForm({}); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const saveStatus = async () => {
    if (!sForm.status) return toast.error("Select a status");
    setBusy(true);
    try {
      await api.post("/hr/payroll/reconciliation/status", { batch_id: batchId, employee_id: statusEdit, ...sForm });
      toast.success("Reconciliation status updated"); setStatusEdit(null); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (recon.rows.length && recon.rows[0].amounts_hidden) {
    return <p className="text-sm text-slate-500" data-testid="recon-hidden">Amounts hidden — requires HR: View Sensitive permission.</p>;
  }

  return (
    <div data-testid="payroll-recon-panel">
      <p className="text-xs text-slate-500 mb-2">Record actual AutoCount payroll per employee, then compare against ERP. ERP is NOT overwritten to match — differences (beyond tolerance RM{recon.tolerance}) are highlighted and must be reviewed/resolved.</p>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full text-xs" data-testid="recon-table">
          <thead><tr><th>Employee</th><th>Status</th><th></th>{fields.map((f) => <th key={f} className="whitespace-nowrap">{RECON_FIELD_LABELS[f]}</th>)}</tr></thead>
          <tbody>
            {recon.rows.map((r) => {
              const rs = RECON_STATUS[r.status] || { label: r.status, cls: "" };
              return [
                <tr key={r.employee_id + "-erp"} data-testid={`recon-row-${r.employee_id}`}>
                  <td rowSpan={2} className="align-top"><span className="font-mono">{r.employee_id}</span> {r.employee_name}
                    {canEdit && <div className="flex flex-col gap-1 mt-1">
                      <Button size="sm" variant="outline" className="h-6 text-xs" data-testid={`recon-edit-${r.employee_id}`} onClick={() => { setEdit(r.employee_id); setForm(r.autocount || {}); }}>Enter AutoCount</Button>
                      {r.has_autocount && <Button size="sm" variant="outline" className="h-6 text-xs" data-testid={`recon-status-${r.employee_id}`} onClick={() => { setStatusEdit(r.employee_id); setSForm({ status: r.status === "pending" ? "" : r.status, reason: r.reason || "", resolution_note: r.resolution_note || "" }); }}>Set Status</Button>}
                    </div>}
                    {r.reviewed_by && <div className="text-[10px] text-slate-400 mt-1">by {r.reviewed_by}</div>}
                  </td>
                  <td rowSpan={2} className="align-top"><Badge variant="outline" className={rs.cls} data-testid={`recon-status-badge-${r.employee_id}`}>{rs.label}</Badge>
                    {r.reason && <div className="text-[10px] text-slate-500 mt-1 max-w-[140px]">{r.reason}</div>}</td>
                  <td className="font-medium text-sky-700">ERP</td>
                  {fields.map((f) => <td key={f}>{fmtRM(r.erp?.[f])}</td>)}
                </tr>,
                <tr key={r.employee_id + "-ac"} className="border-b-2 border-slate-200">
                  <td className="font-medium text-slate-500">AutoCount{r.differences ? " / Δ" : ""}</td>
                  {fields.map((f) => (
                    <td key={f} className={r.material && r.material[f] ? "bg-rose-50" : ""}>
                      {r.has_autocount ? fmtRM(r.autocount?.[f]) : "—"}
                      {r.differences && r.differences[f] !== null && Math.abs(r.differences[f]) > 0.001 &&
                        <span className={`block ${r.material && r.material[f] ? "text-rose-600 font-semibold" : "text-slate-400"}`}>Δ {fmtRM(r.differences[f])}</span>}
                    </td>
                  ))}
                </tr>
              ];
            })}
            {recon.rows.length === 0 && <tr><td colSpan={fields.length + 3} className="text-center text-slate-400 py-6">No calculated lines yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Dialog open onOpenChange={(v) => !v && setEdit(null)}>
          <DialogContent className="max-w-lg" data-testid="recon-edit-dialog">
            <DialogHeader><DialogTitle>AutoCount Payroll — {edit}</DialogTitle>
              <DialogDescription>Enter the actual AutoCount figures for comparison.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto">
              {fields.map((f) => (
                <div key={f}><label className="text-xs text-slate-500">{RECON_FIELD_LABELS[f]}</label>
                  <Input type="number" step="0.01" value={form[f] ?? ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`recon-input-${f}`} /></div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
              <Button className="bg-slate-900" disabled={busy} onClick={saveAc} data-testid="recon-save">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {statusEdit && (
        <Dialog open onOpenChange={(v) => !v && setStatusEdit(null)}>
          <DialogContent className="max-w-md" data-testid="recon-status-dialog">
            <DialogHeader><DialogTitle>Reconciliation Status — {statusEdit}</DialogTitle>
              <DialogDescription>Record the comparison outcome with a reason and resolution note (history preserved).</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><label className="text-xs text-slate-500">Status</label>
                <Select value={sForm.status} onValueChange={(v) => setSForm({ ...sForm, status: v })}>
                  <SelectTrigger data-testid="recon-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>{(recon.statuses || []).map((s) => <SelectItem key={s} value={s}>{(RECON_STATUS[s] || {}).label || s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs text-slate-500">Difference Reason</label>
                <Textarea rows={2} value={sForm.reason} onChange={(e) => setSForm({ ...sForm, reason: e.target.value })} data-testid="recon-reason-input" placeholder="e.g. ERP Attendance Allowance calculated from approved ERP Leave." /></div>
              <div><label className="text-xs text-slate-500">Resolution Note</label>
                <Textarea rows={2} value={sForm.resolution_note} onChange={(e) => setSForm({ ...sForm, resolution_note: e.target.value })} data-testid="recon-note-input" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setStatusEdit(null)}>Cancel</Button>
              <Button className="bg-slate-900" disabled={busy} onClick={saveStatus} data-testid="recon-status-save">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
